/**
 * Витрина кейсов.
 *
 * Сетка, а не горизонтальная лента: кейсов много, и в ленте половина из них
 * прячется за краем экрана — игрок не видит, что каталог вообще больше трёх
 * карточек. Здесь всё помещается по ширине и листается привычным вертикальным
 * скроллом.
 *
 * Группировка приходит с сервера отдельным списком коллекций, поэтому новая
 * витрина добавляется строкой в базу, а не правкой этого файла.
 */

import { formatPaws, type CaseCollectionDto, type CaseDto } from "@koshkodrop/shared";

import { haptic } from "../../lib/telegram";

import { RARITY_COLOR } from "./rarity";

interface Props {
  collections: CaseCollectionDto[];
  cases: CaseDto[];
  balance: bigint;
  onPick: (item: CaseDto) => void;
}

export function CaseCatalog({ collections, cases, balance, onPick }: Props) {
  // Кейс без своей коллекции всё равно должен попасть на витрину: пропажа
  // карточки заметнее, чем лишняя секция «Прочее»
  const known = new Set(collections.map((collection) => collection.code));
  const groups = collections.map((collection) => ({
    collection,
    items: cases.filter((item) => item.collection === collection.code),
  }));
  const orphans = cases.filter((item) => !known.has(item.collection));
  if (orphans.length > 0) {
    groups.push({
      collection: { code: "other", title: "Прочее", subtitle: null },
      items: orphans,
    });
  }

  return (
    <div className="space-y-6">
      {groups
        .filter((group) => group.items.length > 0)
        .map((group) => (
          <section key={group.collection.code} className="space-y-2.5">
            <header className="px-0.5">
              <h3 className="text-sm font-extrabold text-white">{group.collection.title}</h3>
              {group.collection.subtitle && (
                <p className="text-[11px] text-white/35">{group.collection.subtitle}</p>
              )}
            </header>

            <div className="grid grid-cols-2 gap-2.5">
              {group.items.map((item) => (
                <CaseCard
                  key={item.slug}
                  item={item}
                  affordable={balance >= BigInt(item.price)}
                  onPick={onPick}
                />
              ))}
            </div>
          </section>
        ))}
    </div>
  );
}

function CaseCard({
  item,
  affordable,
  onPick,
}: {
  item: CaseDto;
  affordable: boolean;
  onPick: (item: CaseDto) => void;
}) {
  const best = item.items.reduce(
    (top, entry) => (top && BigInt(top.value) >= BigInt(entry.value) ? top : entry),
    item.items[0],
  );

  return (
    <button
      type="button"
      disabled={!item.available}
      onClick={() => {
        haptic.select();
        onPick(item);
      }}
      className="relative overflow-hidden rounded-3xl border border-white/8 p-2.5 text-left transition-transform active:scale-[0.97] disabled:active:scale-100"
      style={{
        background: `linear-gradient(168deg, color-mix(in oklab, ${item.accent} 20%, #14123098), #0b0a20)`,
      }}
    >
      {!item.available && (
        <span className="absolute right-2 top-2 z-10 rounded-md bg-night-950/85 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white/55">
          закрыт
        </span>
      )}

      <div
        className="flex h-[84px] items-center justify-center rounded-2xl"
        style={{
          background: `radial-gradient(circle at 50% 60%, color-mix(in oklab, ${item.accent} 38%, transparent), transparent 70%)`,
        }}
      >
        <span
          className="text-[44px] leading-none"
          style={{
            filter: `drop-shadow(0 6px 16px color-mix(in oklab, ${item.accent} 65%, transparent))`,
            opacity: item.available ? (affordable ? 1 : 0.55) : 0.3,
          }}
        >
          {item.emoji}
        </span>
      </div>

      <p className="mt-2 truncate text-[13px] font-extrabold text-white">{item.title}</p>

      {best && (
        <p className="tabular mt-0.5 truncate text-[10px] font-bold text-white/35">
          до {formatPaws(BigInt(best.value))}{" "}
          <span style={{ color: RARITY_COLOR[best.rarity] }}>{best.emoji}</span>
        </p>
      )}

      <p
        className="tabular mt-1.5 inline-flex rounded-lg px-2 py-0.5 text-[11px] font-extrabold"
        style={{
          background: affordable ? item.accent : "rgba(255,255,255,0.08)",
          color: affordable ? "#07061a" : "rgba(255,255,255,0.55)",
        }}
      >
        {formatPaws(BigInt(item.price))} 🐾
      </p>
    </button>
  );
}
